// src/components/dnl/LineLabelsRenderer.tsx
/**
 * Line Labels Renderer Component
 * Phase 1: Custom/Sparse Points System
 * 
 * Renders contextual text labels at the edge of each number line.
 * Examples: "People", "ml sauce", "Amount (g)"
 * 
 * Based on DNL Enhancement Specification v1.0
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import type { LineLabel } from '../../types/dnl';

// =============================================================================
// TYPES
// =============================================================================

export interface LineLabelsRendererProps {
  /** Whether the number line is horizontal */
  isHorizontal: boolean;
  /** Primary line Y position (for horizontal) or X position (for vertical) */
  primaryLinePos: number;
  /** Secondary line Y position (for horizontal) or X position (for vertical) */
  secondaryLinePos: number;
  /** Left/top edge position of the line */
  lineStart: number;
  /** Canvas width */
  width: number;
  /** Canvas height */
  height: number;
}

// =============================================================================
// STYLING CONSTANTS
// =============================================================================

const LABEL_FONT_SIZE = 14;
const LABEL_PADDING = 8;
const SUBTEXT_FONT_SIZE = 11;
const BACKGROUND_PADDING = 6;
const BACKGROUND_RADIUS = 4;

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Individual line label with optional background
 */
const LineLabelElement: React.FC<{
  label: LineLabel;
  x: number;
  y: number;
  isHorizontal: boolean;
  isDark: boolean;
}> = ({ label, x, y, isHorizontal, isDark }) => {
  if (!label.visible || !label.text) {
    return null;
  }

  const textColor = isDark ? '#E5E7EB' : '#374151';
  const subtextColor = isDark ? '#9CA3AF' : '#6B7280';
  const bgColor = isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)';
  const borderColor = isDark ? '#374151' : '#E5E7EB';

  // Calculate text anchor based on position
  const textAnchor = label.position === 'left' ? 'start' : 'end';

  // Adjust position based on label position setting
  const adjustedX = label.position === 'left' 
    ? x + LABEL_PADDING 
    : x - LABEL_PADDING;

  // For vertical orientation, rotate the label
  const transform = isHorizontal
    ? undefined
    : `rotate(-90, ${adjustedX}, ${y})`;

  return (
    <g className="line-label" transform={transform}>
      {/* Background rectangle */}
      <rect
        x={textAnchor === 'start' ? adjustedX - BACKGROUND_PADDING : adjustedX - 100}
        y={y - LABEL_FONT_SIZE / 2 - BACKGROUND_PADDING}
        width={textAnchor === 'start' ? 100 : 100 + BACKGROUND_PADDING}
        height={label.subtext 
          ? LABEL_FONT_SIZE + SUBTEXT_FONT_SIZE + BACKGROUND_PADDING * 2 + 4
          : LABEL_FONT_SIZE + BACKGROUND_PADDING * 2
        }
        rx={BACKGROUND_RADIUS}
        fill={bgColor}
        stroke={borderColor}
        strokeWidth={1}
        opacity={0.95}
      />
      
      {/* Main label text */}
      <text
        x={adjustedX}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fill={textColor}
        fontSize={LABEL_FONT_SIZE}
        fontWeight={600}
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {label.text}
      </text>

      {/* Subtext (e.g., unit) */}
      {label.subtext && (
        <text
          x={adjustedX}
          y={y + LABEL_FONT_SIZE}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fill={subtextColor}
          fontSize={SUBTEXT_FONT_SIZE}
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {label.subtext}
        </text>
      )}
    </g>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const LineLabelsRenderer: React.FC<LineLabelsRendererProps> = ({
  isHorizontal,
  primaryLinePos,
  secondaryLinePos,
  lineStart,
  width,
  height,
}) => {
  // Get labels from Redux
  const primaryLabel = useSelector((state: RootState) => state.dnlTools.primaryLabel);
  const secondaryLabel = useSelector((state: RootState) => state.dnlTools.secondaryLabel);
  const isDark = useSelector((state: RootState) => state.preferences.theme === 'dark');
  const secondaryLineEnabled = useSelector((state: RootState) => state.secondaryLine.enabled);

  // Get current view and animations state for transitions
  const currentView = useSelector((state: RootState) => 
    state.dnlTools.alternativeViews.currentView
  );
  const animationsEnabled = useSelector((state: RootState) => 
    state.dnlTools.alternativeViews.animationsEnabled
  );

  // Track view changes for animations
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevViewRef = useRef(currentView);
  const prevPrimaryTextRef = useRef(primaryLabel.text);
  const prevSecondaryTextRef = useRef(secondaryLabel.text);

  // Watch for view changes or label text changes
  useEffect(() => {
    const viewChanged = prevViewRef.current !== currentView;
    const primaryTextChanged = prevPrimaryTextRef.current !== primaryLabel.text;
    const secondaryTextChanged = prevSecondaryTextRef.current !== secondaryLabel.text;

    if (animationsEnabled && (viewChanged || primaryTextChanged || secondaryTextChanged)) {
      setIsTransitioning(true);
      prevViewRef.current = currentView;
      prevPrimaryTextRef.current = primaryLabel.text;
      prevSecondaryTextRef.current = secondaryLabel.text;
      
      // Reset transition state after animation completes
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [currentView, animationsEnabled, primaryLabel.text, secondaryLabel.text]);

  // Calculate label positions
  const labelPositions = useMemo(() => {
    if (isHorizontal) {
      // For horizontal lines, labels go at the left edge
      const x = primaryLabel.position === 'left' ? lineStart : width - lineStart;
      return {
        primary: { x, y: primaryLinePos },
        secondary: { x, y: secondaryLinePos },
      };
    } else {
      // For vertical lines, labels go at the top edge
      const y = primaryLabel.position === 'left' ? lineStart : height - lineStart;
      return {
        primary: { x: primaryLinePos, y },
        secondary: { x: secondaryLinePos, y },
      };
    }
  }, [isHorizontal, primaryLinePos, secondaryLinePos, lineStart, width, height, primaryLabel.position]);

  // Check if any labels are visible
  const hasVisibleLabels = primaryLabel.visible || (secondaryLineEnabled && secondaryLabel.visible);

  if (!hasVisibleLabels) {
    return null;
  }

  return (
    <g className="line-labels-layer">
      {/* Primary line label */}
      {primaryLabel.visible && (
        <g className={`line-label ${isTransitioning ? 'transitioning' : ''}`}>
          <LineLabelElement
            label={primaryLabel}
            x={labelPositions.primary.x}
            y={labelPositions.primary.y}
            isHorizontal={isHorizontal}
            isDark={isDark}
          />
        </g>
      )}

      {/* Secondary line label */}
      {secondaryLineEnabled && secondaryLabel.visible && (
        <g className={`line-label ${isTransitioning ? 'transitioning' : ''}`}>
          <LineLabelElement
            label={secondaryLabel}
            x={labelPositions.secondary.x}
            y={labelPositions.secondary.y}
            isHorizontal={isHorizontal}
            isDark={isDark}
          />
        </g>
      )}
    </g>
  );
};

export default LineLabelsRenderer;
